import { useCallback } from "react"

import { useLocation, useNavigate } from "react-router-dom"

import type { SolarProposalDto } from "~/api/dto/solar-proposal.dto"
import { Drawer } from "~/components/ui-components/v2"

import { SolarHdmStep } from "./hdm/SolarHdmStep"
import { solarPricingBundle } from "./solar-pricing-bundle"
import {
  SOLAR_SETUP_DRAWER_TITLES,
  solarSetupGroupForStep,
  solarSetupGroupPath,
  solarSetupStepFromPath,
  type SolarSetupStep,
} from "./solar-setup-paths"
import { SolarComputeStep } from "./SolarComputeStep"
import { SolarConsumptionStep } from "./SolarConsumptionStep"
import { SolarDesignStep } from "./SolarDesignStep"
import { SolarEquipmentStep } from "./SolarEquipmentStep"
import { SolarGreenbuttonStep } from "./SolarGreenbuttonStep"
import { SolarLoanProductStep } from "./SolarLoanProductStep"
import { SolarSiteStep } from "./SolarSiteStep"
import { SolarSystemPricingStep } from "./SolarSystemPricingStep"
import { SolarUtilityStep } from "./SolarUtilityStep"
import type { PricingKindBundle } from "../ProposalSetup/pricing/pricing-kind-bundle"
import { PricingAddersStep } from "../ProposalSetup/pricing/PricingAddersStep"
import { PricingIncentivesStep } from "../ProposalSetup/pricing/PricingIncentivesStep"
import { PricingVppProgramsStep } from "../ProposalSetup/pricing/PricingVppProgramsStep"
import { PricingWarrantyStep } from "../ProposalSetup/pricing/PricingWarrantyStep"

type PricingStepComponent = (props: {
  kind: PricingKindBundle
  proposalId: string
  onClose: () => void
}) => JSX.Element

/** The pricing steps Solar shares with Battery, by step. */
const SOLAR_PRICING_STEPS: Partial<
  Record<SolarSetupStep, PricingStepComponent>
> = {
  adders: PricingAddersStep,
  warranty: PricingWarrantyStep,
  incentives: PricingIncentivesStep,
  [`vpp-programs`]: PricingVppProgramsStep,
}

/**
 * The open step's drawer, with Battery's step-drawer chrome, width and layer.
 * Closing returns to the step's group in the rail. Site Information renders
 * from the header read the shell already holds; Utility Company & Rate,
 * Consumption, Finance Partner & Equipment, Compute and System Pricing read their own.
 * Adders, Protection Plans, Incentives and VPP Programs are the pricing steps
 * shared with Battery, over Solar's bundle. Green Button Data is V1's drawer,
 * with its own chrome. HDM (under Contract) reads its own and calls HDM.
 */
export const SolarSetupDrawer = ({
  proposal,
  proposalId,
}: {
  proposal: SolarProposalDto
  proposalId: string
}): JSX.Element | null => {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const step = solarSetupStepFromPath(pathname, proposalId)

  const onClose = useCallback(() => {
    if (step) {
      navigate(solarSetupGroupPath(proposalId, solarSetupGroupForStep(step).id))
    }
  }, [navigate, proposalId, step])

  // Contract and Financing Status render from their routes, so a read-only
  // viewer can open them too.
  if (!step || step === `contract` || step === `financing-status`) {
    return null
  }

  // The shared pricing steps bring their own drawer, as Battery's do.
  const PricingStep = SOLAR_PRICING_STEPS[step]
  if (PricingStep) {
    return (
      <PricingStep
        kind={solarPricingBundle}
        proposalId={proposalId}
        onClose={onClose}
      />
    )
  }

  // Compute brings the pricing steps' card drawer.
  if (step === `compute`) {
    return <SolarComputeStep proposalId={proposalId} onClose={onClose} />
  }

  // Credit Human's Loan Product step brings the shared picker in its own drawer.
  if (step === `loan-product`) {
    return <SolarLoanProductStep proposalId={proposalId} onClose={onClose} />
  }

  // The Green Button drawer brings its own full-width drawer chrome.
  if (step === `greenbutton`) {
    return (
      <SolarGreenbuttonStep
        onClose={onClose}
        proposal={proposal}
        proposalId={proposalId}
      />
    )
  }

  // System Pricing is Battery's pricing drawer: half the viewport from sm, the
  // full width on phones, with the breakdown scrolling inside.
  const isSystemPricing = step === `system-pricing`
  const isHdm = step === `hdm`

  return (
    <Drawer
      open
      theme="dark"
      onClose={onClose}
      title={SOLAR_SETUP_DRAWER_TITLES[step]}
      widthClass={
        isSystemPricing
          ? `sm:max-w-[50vw]`
          : isHdm
            ? `sm:max-w-2xl`
            : `sm:max-w-lg`
      }
      contentClassName={
        isSystemPricing ? `flex min-h-0 flex-col overflow-hidden` : undefined
      }
    >
      {step === `equipment` ? (
        <SolarEquipmentStep
          proposalId={proposalId}
          onClose={onClose}
          onSaved={onClose}
        />
      ) : step === `utility` ? (
        <SolarUtilityStep proposalId={proposalId} />
      ) : step === `consumption` ? (
        <SolarConsumptionStep proposalId={proposalId} />
      ) : step === `system-pricing` ? (
        <SolarSystemPricingStep proposalId={proposalId} onClose={onClose} />
      ) : step === `hdm` ? (
        <SolarHdmStep proposalId={proposalId} />
      ) : step === `design` ? (
        // Keyed: its production-pending state belongs to this proposal.
        <SolarDesignStep key={proposal.id} proposal={proposal} />
      ) : (
        <SolarSiteStep proposal={proposal} />
      )}
    </Drawer>
  )
}
